import React from "react";
import { Field, reduxForm } from "redux-form";
import { Row, Col } from "react-bootstrap";
import moment from "moment";
import classnames from "classnames";

import Button from "../../../../../../components/common/button/Button";
import DatePicker2 from "../../../../../../components/common/datepicker/DatePicker2";
import Input from "../../../../../../components/common/input/Input";
import RichEditor from "../../../../../../components/common/richEditor/RichEditor";
import Select2 from "../../../../../../components/common/select/Select2";
import Typeahead from "../../../../../../components/common/typeahead/Typeahead";

import {
  required,
  requiredRichEditor,
  dropdown,
  date,
  floatWithComma,
  comma
} from "../../../../../../utils/forms/validators";

import { Mixpanel } from "../../../../../../actions/mixpanel.actions";
import { PROJECT_SOW_FORM } from "../../../../../../actions/project.actions";

const rateTypeVal = dropdown("Please select a Rate Type.");
const rateFloat = floatWithComma("Please enter a valid number.");
const dateVal = date("Please enter a valid Date.");
const rateComma = comma("Please enter numeric characters only");

// const noPrevDate = startDate => value => {
//   const validate = moment(value).isAfter(moment(startDate))
//     ? undefined
//     : "End date must be after start date.";
//   return validate;
// };

import styles from "./ProjectSOWEdit.scss";

class ProjectSOW extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      rate: null,
      startDate: moment(),
      endDate: undefined,
      valid: true
    };

    this.handleStartDateChange = this.handleStartDateChange.bind(this);
    this.handleEndDateChange = this.handleEndDateChange.bind(this);
  }

  componentDidMount() {
    this.updateForm();
  }

  componentDidUpdate(prevProps) {
    const { sow } = this.props;

    if (sow !== prevProps.sow) {
      this.updateForm();
    }
  }

  updateForm() {
    const { sow, change } = this.props;

    if (!_.isEmpty(sow)) {
      change("summary", sow.summary);
      change("deliverables", sow.deliverables);
      change("work_type_id", sow.work_type_id);
      change("tax_status_id", sow.tax_status_id);
      change("project_start", sow.project_start);
      change("project_end", sow.project_end);
      change("city", sow.city);
      change("project_period", sow.project_period);
      change("project_flexible", sow.project_flexible);
      change("budget_rate_type_id", sow.budget_rate_type_id);
      change("budget_rate_value", sow.budget_rate_value);
    }
  }

  handleEndDateChange(date) {
    let newDate = moment(date);

    if (newDate.isValid()) {
      this.setState({ endDate: moment(newDate) });

      this.setState(prevState => {
        const valid = moment(newDate).isAfter(moment(prevState.startDate));
        return { valid: valid };
      });
    }
  }

  handleStartDateChange(date) {
    let newDate = moment(date);

    if (newDate.isValid()) {
      this.setState({ startDate: moment(newDate) });

      if (
        this.state.endDate !== undefined &&
        moment(newDate).isAfter(this.state.endDate)
      ) {
        this.props.change("project_end", null);
      }
    }
  }

  render() {
    const {
      invalid,
      submitting,
      pristine,
      handleSubmit,
      disabled,
      sow,
      onSearchCities,
      cities
    } = this.props;

    return (
      <form
        className={classnames(styles.projectSow, {
          [styles.disabledPage]: disabled
        })}
        onSubmit={handleSubmit}
      >
        {disabled && <div className={styles.disabled} />}
        <div className={styles.titleBlock}>
          <h1>Statement Of Work</h1>
        </div>

        <Row className={styles.whiteSection}>
          <h2 className={styles.headerText}>Project Summary</h2>
          Please provide a brief description of the project. You can use the
          description provided by the company if applicable.
          <div className={styles.richEditor}>
            <Field
              name="summary"
              component={RichEditor}
              validate={[requiredRichEditor]}
              props={{ maxLength: 1501 }}
            />
          </div>
        </Row>

        <Row className={styles.whiteSection}>
          <h2 className={styles.headerText}>Deliverables + Milestones</h2>
          Describe the work you will perform for the company, including key
          deliverables and milestones you’ll work toward throughout the duration
          of the project.
          <div className={styles.richEditor}>
            <Field
              name="deliverables"
              component={RichEditor}
              validate={[requiredRichEditor]}
              props={{ maxLength: 1501 }}
            />
          </div>
        </Row>

        <Row className={styles.whiteSection}>
          <h2 className={styles.headerText}>Client Office Location</h2>
          <Col xs={5}>
            <Field
              name="city"
              placeholder="City, State"
              component={Typeahead}
              onSearch={query => onSearchCities(query)}
              isLoading={false}
              options={cities}
              isAsync={true}
              minLength={3}
              allowNew={false}
              labelKey="description"
              valueKey="id"
              validate={required}
              icon="map-marker"
              selected={sow && sow.city ? [sow.city] : null}
            />
          </Col>
          <Col xs={1} />
          <Col xs={5}>
            <Field
              name="work_type_id"
              type="select"
              placeholder="Work From..."
              component={Select2}
              options={this.props.workTypes}
            />
          </Col>
        </Row>

        <Row className={styles.whiteSection}>
          <h2 className={styles.headerText}>Tax Status</h2>
          <Col xs={4}>
            <Field
              name="tax_status_id"
              type="select"
              placeholder="Tax Status"
              component={Select2}
              options={this.props.taxStatus}
            />
          </Col>
        </Row>

        <Row className={styles.whiteSection}>
          <h2 className={styles.headerText}>Project Duration</h2>
          <div>
            <Col xs={3}>
              <Field
                name="project_start"
                placeholder="Start"
                component={DatePicker2}
                minDate={moment()}
                onSelectDate={this.handleStartDateChange}
                validate={dateVal}
              />
            </Col>
            <Col xs={1} />
            <Col xs={3}>
              <Field
                name="project_end"
                placeholder="End"
                component={DatePicker2}
                minDate={this.state.startDate}
                onSelectDate={this.handleEndDateChange}
                validate={dateVal}
              />
            </Col>
            <Col xs={1} />
            <Col xs={3}>
              <Field
                name="project_period"
                type="text"
                placeholder="Hours/Week"
                component={Input}
                onChange={this.handleRateChange}
                validate={[required, rateFloat]}
              />
            </Col>
          </div>
          <div />
        </Row>

        <Row className={styles.whiteSection}>
          <h2 className={styles.headerText}>Budget</h2>
          <Col xs={4}>
            <Field
              name="budget_rate_type_id"
              type="select"
              placeholder="Rate Type"
              component={Select2}
              options={this.props.projectBudgetRateTypes}
              validate={[required, rateTypeVal]}
            />
          </Col>
          <Col xs={1} />
          <Col xs={4}>
            <Field
              name="budget_rate_value"
              type="text"
              placeholder="Rate"
              component={Input}
              onChange={this.handleRateChange}
              validate={[required, rateFloat, rateComma]}
            />
          </Col>
          <Col xs={12} className={styles.blueSection}>
            Once paid by the client, The Second Shift will pay you the invoiced
            amount less a 5% service fee.
          </Col>
        </Row>

        <Row>
          <div className={styles.doubleDivider} />
        </Row>

        <Row className={classnames(styles.whiteSection, styles.termsOfService)}>
          <div className={classnames(styles.subSectionHeader, styles.title)}>
            Terms of Service
          </div>
          <div className={styles.text}>
            Before submitting your Statement of Work, please remember that you
            have already agreed to our Terms of Service when you
            registered.&nbsp;
            <a
              href="https://www.thesecondshift.com/tos"
              onClick={() => Mixpanel.track("Member - SOW - Read TOS")}
            >
              Read in full.
            </a>
          </div>

          <Button
            isDisabled={
              invalid || submitting || pristine || disabled || !this.state.valid
            }
            isPrimary
            type="submit"
            size="large"
            label="Submit to Company"
            onClick={() => Mixpanel.track("Member - SOW - Submit")}
          />
        </Row>
      </form>
    );
  }
}

ProjectSOW = reduxForm({
  form: PROJECT_SOW_FORM
})(ProjectSOW);

export default ProjectSOW;



// WEBPACK FOOTER //
// ./src/routes/candidate/project/statementOfWork/components/ProjectSOW/ProjectSOWEdit.js